import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import axios from 'axios';
import {
  selectAuthUserName,
  selectGroup,
  selectUser,
  useAppSelector,
} from '../../slices/commonSelectors';
import apiConfig from '../../constants/apiConfig';
import StylishNewSelect from '../DesignSystems/New/StylishNewSelect';
import { useIsDev } from '../../hooks/useIsDev';
import SubscriptionStatusCard from 'components/SubscriptionManagement/SubscriptionStatusCard';
import useIsOrgAdmin from '../../hooks/useIsOrgAdmin';
import {
  createOrGetUser,
  updateCurrentlySelected,
} from '../../actions/profileActions';
import {
  statusDisplayOptions,
  statusOptions,
  getDisplayStatus,
} from 'utils/subscriptionStatus';
import { Subscription } from 'types/subscription';

const OrgSubscription: React.FC = () => {
  const isDev = useIsDev();
  const selectedGroup = useAppSelector(selectGroup);
  const user = useAppSelector(selectUser);
  const userName = useAppSelector(selectAuthUserName);
  const dispatch = useDispatch();

  const isOrgAdmin = useIsOrgAdmin();

  const groupSubscription = user.subscriptions?.find(
    (s: Subscription) => s.group_guid === selectedGroup.group_guid
  );

  const [subscriptionStatus, setSubscriptionStatus] = useState(
    groupSubscription?.status ?? statusOptions[0]
  );

  // No dedicated "refetch subscriptions" endpoint exists; createOrGetUser is the only path
  // that repopulates user.subscriptions (see coms DICEFnComponents.createOrGetUserByEmail).
  const refreshSubscriptions = () => {
    if (!userName) return undefined;
    return dispatch(
      createOrGetUser({ userName })
    ) as unknown as Promise<unknown>;
  };

  // Persist the org the user is working on so a manual page refresh stays here
  // (covers users who reached this page without switching via the header, e.g. after org creation).
  const persistCurrentOrgIfNeeded = () => {
    const persistedGuid = user.currently_selected?.group_guid;
    if (
      selectedGroup?.group_guid &&
      selectedGroup.group_guid !== persistedGuid
    ) {
      dispatch(
        updateCurrentlySelected({
          groupId: selectedGroup.group_guid,
        } as any)
      );
    }
  };

  const createTrialSubscription = useMutation({
    mutationFn: async () => {
      const response = await axios.post(
        apiConfig.apiGateway.createOrgSubscription,
        {
          group_guid: selectedGroup.group_guid,
          details: {
            subscription_type: 'defaults to trial subscription',
          },
        }
      );
      return response.data;
    },
    // Await the refresh so the mutation stays pending until user.subscriptions is repopulated;
    // this keeps the button disabled long enough to prevent a duplicate create on double-click.
    onSuccess: async () => {
      persistCurrentOrgIfNeeded();
      await refreshSubscriptions();
      toast.success('Trial subscription created.');
    },
  });

  const updateSubscription = useMutation({
    mutationFn: async (status: string) => {
      const response = await axios.post(
        apiConfig.apiGateway.updateOrgSubscription,
        {
          group_guid: selectedGroup.group_guid,
          status,
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      persistCurrentOrgIfNeeded();
      await refreshSubscriptions();
      toast.success('Subscription status updated.');
    },
  });

  return (
    <>
      <div className="nav-content-header">Subscription Information</div>
      <div className="card mt-2">
        <div className="d-flex flex-md-row flex-column w-100 gap-3">
          <div className="d-flex flex-column">
            <div className={'alert'}>
              <strong>Current subscription</strong>
              {groupSubscription ? (
                <div>
                  <span>{groupSubscription.group_name}:</span>
                  <span className={'text-muted ps-2'}>
                    {getDisplayStatus(groupSubscription.status)}
                  </span>
                  {isOrgAdmin && (
                    <div>
                      <span>Total Seats: </span>
                      <span className="text-muted">
                        {groupSubscription?.quantity || 'Not specified'}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-2">
                  No subscription found for this organization
                </p>
              )}
            </div>
            {groupSubscription && isDev ? (
              <>
                <StylishNewSelect
                  classes={'secondary'}
                  options={statusDisplayOptions}
                  value={statusDisplayOptions.find(
                    (opt) => opt.value === subscriptionStatus
                  )}
                  placeholder="Select a Status"
                  onChange={(e) => setSubscriptionStatus(e.value)}
                />

                <StylishNewButton
                  className={'button--secondary mt-3'}
                  disabled={updateSubscription.isPending}
                  onClick={() => updateSubscription.mutate(subscriptionStatus)}
                >
                  Save
                </StylishNewButton>
              </>
            ) : null}

            {!groupSubscription ? (
              <StylishNewButton
                className={'button--secondary'}
                disabled={createTrialSubscription.isPending}
                onClick={() => createTrialSubscription.mutate()}
              >
                Create Trial Subscription
              </StylishNewButton>
            ) : null}
          </div>
        </div>
      </div>
      {user.subscription && <SubscriptionStatusCard />}
    </>
  );
};

export default OrgSubscription;
